/**
 * Hooks spécialisés pour les permissions sur les ressources
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMultipleAuthorizations } from './useAuthorization';
import { authorizationService } from '../services/authorizationService';
import { ActionType, ModelType } from '@constants/authorization';

/**
 * Hook pour toutes les permissions sur une ressource
 */
export const useResourcePermissions = (resourceId, resource = null) => {
  const [permissions, setPermissions] = useState({
    canUpdate: false,
    canDelete: false,
    canBorrow: false,
    canConsume: false,
    canRefill: false,
    canReserve: false,
    canReturn: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkPermissions = useCallback(async () => {
    if (!resourceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get all resource permissions in one call
      const resourcePermissions = await authorizationService.getAllPermissions(
        ModelType.RESOURCE,
        resourceId
      );

      const mappedPermissions = {
        canUpdate: resourcePermissions.update || false,
        canDelete: resourcePermissions.delete || false,
        canBorrow: resourcePermissions.borrow || false,
        canConsume: resourcePermissions.consume || false,
        canRefill: resourcePermissions.refill || false,
        canReserve: resourcePermissions.reserve || false,
        canReturn: resourcePermissions.return || false
      };

      setPermissions(mappedPermissions);
    } catch (err) {
      console.error('Failed to check resource permissions:', err);
      setError(err);
      setPermissions({
        canUpdate: false,
        canDelete: false,
        canBorrow: false,
        canConsume: false,
        canRefill: false,
        canReserve: false,
        canReturn: false
      });
    } finally {
      setLoading(false);
    }
  }, [resourceId, resource?.current_borrowing?.id, resource?.consumable, resource?.is_currently_borrowed, resource?.current_quantity, resource?.is_available]);

  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  const refresh = useCallback(() => {
    checkPermissions();
  }, [checkPermissions]);

  return {
    permissions,
    loading,
    error,
    refresh
  };
};

/**
 * Hook pour les permissions de création de ressources
 */
export const useResourceCreationPermission = () => {
  const checks = useMemo(() => [
    { key: 'canCreate', action: ActionType.CREATE, modelType: ModelType.RESOURCE }
  ], []);

  const { permissions, loading, error, refresh } = useMultipleAuthorizations(checks);

  return {
    canCreate: permissions.canCreate || false,
    loading,
    error,
    refresh
  };
};

/**
 * Hook spécialisé pour les permissions d'emprunt avec délégation
 */
export const useBorrowPermissions = (resourceId, targetUserId = null) => {
  const checks = useMemo(() => {
    if (!resourceId) return [];

    const context = targetUserId ? { target_user_id: targetUserId } : {};

    return [
      {
        key: 'canBorrow',
        action: ActionType.BORROW,
        modelType: ModelType.RESOURCE,
        modelId: resourceId,
        context
      }
    ];
  }, [resourceId, targetUserId]);

  const { permissions, loading, error, refresh } = useMultipleAuthorizations(checks);

  return {
    canBorrow: permissions.canBorrow || false,
    loading,
    error,
    refresh
  };
};

/**
 * Hook pour les permissions de consommation avec délégation
 */
export const useConsumePermissions = (resourceId, targetUserId = null) => {
  const checks = useMemo(() => {
    if (!resourceId) return [];

    const context = targetUserId ? { target_user_id: targetUserId } : {};

    return [
      {
        key: 'canConsume',
        action: ActionType.CONSUME,
        modelType: ModelType.RESOURCE,
        modelId: resourceId,
        context
      }
    ];
  }, [resourceId, targetUserId]);

  const { permissions, loading, error, refresh } = useMultipleAuthorizations(checks);

  return {
    canConsume: permissions.canConsume || false,
    loading,
    error,
    refresh
  };
};
